import React, { useState, useEffect, useCallback } from 'react'
import styles from './ActionAccessManagement.module.css'
import {
    FiToggleLeft, FiToggleRight, FiChevronRight, FiSearch,
    FiLayers, FiShield, FiAlertCircle, FiLoader, FiCheck,
    FiX, FiZap
} from 'react-icons/fi'
import { useParams } from 'react-router-dom'
import { BASE_URL_API } from '../../../References/URLs'
import { useContext } from 'react'
import AuthContext from '../../../store/auth-context'


// ── Simulated / mock data for preview ─────────────────────────────────────────
// Matches the real API response shape:
//   featureLabel   → display name
//   pageUrl        → used as the grouping key (e.g. "/overview", "/add-clients")
//   featureIdentifier → unique key
//   isAccessible   → STRING "true" / "false"  ← important!
const MOCK_FEATURES = [
    { featureLabel: 'View Dashboard',         pageUrl: '/overview',                        featureIdentifier: 'overview_viewDashboard',                      isAccessible: 'true'  },
    { featureLabel: 'Create A Bill Button',   pageUrl: '/overview',                        featureIdentifier: 'overview_createABill',                        isAccessible: 'false' },
    { featureLabel: 'Add New Client',         pageUrl: '/add-clients',                     featureIdentifier: 'add-clients_createProfile',                   isAccessible: 'false' },
    { featureLabel: 'View Booking Details',   pageUrl: '/booking/:bookingIdentifier',      featureIdentifier: 'booking/:bookingIdentifier_viewDetails',      isAccessible: 'false' },
    { featureLabel: 'Cancel Appointment',     pageUrl: '/appointments',                    featureIdentifier: 'appointments_cancel',                         isAccessible: 'true'  },
    { featureLabel: 'Raise Invoice',          pageUrl: '/billing',                         featureIdentifier: 'billing_raiseInvoice',                        isAccessible: 'true'  },
    { featureLabel: 'Delete Invoice',         pageUrl: '/billing',                         featureIdentifier: 'billing_deleteInvoice',                       isAccessible: 'false' },
    { featureLabel: 'Add Staff',              pageUrl: '/staff-management',                featureIdentifier: 'staff_addStaff',                              isAccessible: 'false' },
    { featureLabel: 'View Staff',             pageUrl: '/staff-management',                featureIdentifier: 'staff_viewStaff',                             isAccessible: 'true'  },
]
// ──────────────────────────────────────────────────────────────────────────────

// ── Helper: normalise isAccessible to boolean regardless of API type ──────────
// The API returns isAccessible as a string "true"/"false" — this handles both
// string and actual boolean defensively.
const toBool = (val) => val === true || val === 'true'


const ActionAccessManagement = ({ storeAccessData }) => {
    const { authIdentifier } = useParams()
    const authCtx = useContext(AuthContext)
    const loginData = JSON.parse(authCtx?.loginData || '{}')
    console.log(storeAccessData)

    const [selectedStore, setSelectedStore] = useState('')
    const [isLoadingData, setIsLoadingData] = useState(false)
    const [featuresAccessData, setFeaturesAccessData] = useState([])
    const [togglingId, setTogglingId] = useState(null)
    const [toast, setToast] = useState(null) // { message, type }
    const [searchQuery, setSearchQuery] = useState('')
    const [expandedPages, setExpandedPages] = useState({})

    // ── Auto-select first store ────────────────────────────────────────────────
    useEffect(() => {
        if (storeAccessData?.length && !selectedStore) {
            setSelectedStore(storeAccessData[0]?.storeIdentifier)
        }
    }, [storeAccessData])

    // ── Show toast ─────────────────────────────────────────────────────────────
    const showToast = (message, type = 'success') => {
        setToast({ message, type })
        setTimeout(() => setToast(null), 2800)
    }

    // ── Fetch features ─────────────────────────────────────────────────────────
    const getFeaturesAccessibilityStatus = useCallback(async () => {
        if (!selectedStore) return
        setIsLoadingData(true)
        try {
            const response = await fetch(BASE_URL_API + 'getFeaturesAccessibilityStatus', {
                method: 'POST',
                body: JSON.stringify({
                    token: authCtx?.token,
                    authIdentifier,
                    storeIdentifier: selectedStore,
                    vendorIdentifier: loginData?.partnersData?.vendorIdentifier
                })
            })
            if (!response.ok) throw new Error('Server error')
            const respo = await response.json()
            if (respo?.status === 'success') {
                setFeaturesAccessData(respo?.response)
            } else if (respo?.message === 'tokenExpired') {
                authCtx.logout()
            }
        } catch {
            // fallback to mock for demo
            setFeaturesAccessData(MOCK_FEATURES)
        } finally {
            setIsLoadingData(false)
        }
    }, [selectedStore, authIdentifier])

    useEffect(() => {
        getFeaturesAccessibilityStatus()
    }, [getFeaturesAccessibilityStatus])

    // ── Toggle feature ─────────────────────────────────────────────────────────
    const toggleFeatureAccessStatus = async (featureIdentifier, feature, currentStatus) => {
        if (togglingId) return
        setTogglingId(featureIdentifier)

        // Optimistic update — keep isAccessible as string to match API shape
        setFeaturesAccessData(prev =>
            prev.map(f =>
                f.featureIdentifier === featureIdentifier
                    ? { ...f, isAccessible: currentStatus ? 'false' : 'true' }
                    : f
            )
        )
        console.log('Toggling', featureIdentifier, 'to', !currentStatus)

        try {
            const response = await fetch(BASE_URL_API + 'toggleFeatureAccessStatus', {
                method: 'POST',
                body: JSON.stringify({
                    token: authCtx?.token,
                    authIdentifier,
                    featureIdentifier,
                    storeIdentifier: selectedStore,
                    pageUrl: feature?.pageUrl,
                    isAccessible: !currentStatus,
                    addedBy : authCtx.authIdentifier
                })
            })
            const respo = await response.json()
            if (respo?.status === 'success') {
                showToast(`Access ${!currentStatus ? 'granted' : 'revoked'} successfully`)
            } else {
                // Rollback — restore original string value
                setFeaturesAccessData(prev =>
                    prev.map(f =>
                        f.featureIdentifier === featureIdentifier
                            ? { ...f, isAccessible: currentStatus ? 'true' : 'false' }
                            : f
                    )
                )
                showToast('Failed to update access', 'error')
            }
        } catch {
            // On network error, rollback
            setFeaturesAccessData(prev =>
                prev.map(f =>
                    f.featureIdentifier === featureIdentifier
                        ? { ...f, isAccessible: currentStatus ? 'true' : 'false' }
                        : f
                )
            )
            showToast('Network error', 'error')
        } finally {
            setTogglingId(null)
        }
    }

    // ── Derived data ───────────────────────────────────────────────────────────
    // featuresAccessData is already scoped to the selected store by the API —
    // it is re-fetched fresh on every store switch, so NO storeIdentifier
    // filtering is needed here at all.
    const storeFeatures = featuresAccessData

    const filteredFeatures = storeFeatures.filter(f =>
        f.featureLabel?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        f.pageUrl?.toLowerCase().includes(searchQuery.toLowerCase())
    )

    // Group by pageUrl (e.g. "/overview", "/billing")
    const groupedByPage = filteredFeatures.reduce((acc, feature) => {
        const page = feature.pageUrl || '/general'
        if (!acc[page]) acc[page] = []
        acc[page].push(feature)
        return acc
    }, {})

    const pages = Object.keys(groupedByPage)

    // Expand all pages initially (resets when store changes because expandedPages resets)
    useEffect(() => {
        if (pages.length && Object.keys(expandedPages).length === 0) {
            const initial = {}
            pages.forEach(p => { initial[p] = true })
            setExpandedPages(initial)
        }
    }, [pages.length])

    const togglePage = (page) => {
        setExpandedPages(prev => ({ ...prev, [page]: !prev[page] }))
    }

    // Stats — isAccessible comes as string "true"/"false" from API, normalise with toBool
    const totalFeatures = storeFeatures.length
    const enabledFeatures = storeFeatures.filter(f => toBool(f.isAccessible)).length
    const disabledFeatures = totalFeatures - enabledFeatures

    const selectedStoreName = storeAccessData?.find(
        s => s.storeIdentifier === selectedStore
    )?.storeName || 'Select a Store'

    return (
        <div className={styles.root}>

            {/* ── Toast ── */}
            {toast && (
                <div className={`${styles.toast} ${styles[`toast_${toast.type}`]}`}>
                    {toast.type === 'success' ? <FiCheck size={13} /> : <FiX size={13} />}
                    <span>{toast.message}</span>
                </div>
            )}

            {/* ── Left panel: Stores ── */}
            <aside className={styles.sidebar}>
                <div className={styles.sidebarHeader}>
                    <FiLayers size={13} />
                    <span>Locations</span>
                </div>
                <div className={styles.storeList}>
                    {storeAccessData?.map((item, index) => {
                        const isActive = item.storeIdentifier === selectedStore
                        // Data is already store-scoped per fetch, so only show badge on active store
                        const storeFeats = isActive ? featuresAccessData : []
                        const enabled = storeFeats.filter(f => toBool(f.isAccessible)).length
                        const total = storeFeats.length

                        return (
                            <button
                                key={index}
                                className={`${styles.storeItem} ${isActive ? styles.storeItemActive : ''} ${item?.isAccessible==="false" ? styles.storeItemDisabled : ''}`}
                                onClick={() => {
                                    setSelectedStore(item.storeIdentifier)
                                    setSearchQuery('')
                                    setExpandedPages({})
                                }}
                                disabled={item?.isAccessible==="false" ? true : false}
                            >
                                <div className={styles.storeItemInner}>
                                    <div className={styles.storeAvatar}>
                                        {item.storeName?.charAt(0)?.toUpperCase()}
                                    </div>
                                    <div className={styles.storeInfo}>
                                        <span className={styles.storeName}>{item.storeName}</span>
                                        <span className={styles.storeId}>{item.storeIdentifier}</span>
                                    </div>
                                </div>
                                {total > 0 && (
                                    <div className={styles.storeBadge}>
                                        <span className={styles.badgeEnabled}>{enabled}</span>
                                        <span className={styles.badgeSep}>/</span>
                                        <span className={styles.badgeTotal}>{total}</span>
                                    </div>
                                )}
                                {isActive && <div className={styles.activeBar} />}
                            </button>
                        )
                    })}
                </div>
            </aside>

            {/* ── Right panel: Features ── */}
            <main className={styles.main}>

                {/* Header */}
                <div className={styles.mainHeader}>
                    <div className={styles.breadcrumb}>
                        <FiShield size={12} />
                        <span>Action Access</span>
                        <FiChevronRight size={11} />
                        <span className={styles.breadcrumbStore}>{selectedStoreName}</span>
                    </div>

                    <div className={styles.statsRow}>
                        <div className={styles.stat}>
                            <span className={styles.statNum}>{totalFeatures}</span>
                            <span className={styles.statLabel}>Total</span>
                        </div>
                        <div className={`${styles.stat} ${styles.statEnabled}`}>
                            <span className={styles.statNum}>{enabledFeatures}</span>
                            <span className={styles.statLabel}>Enabled</span>
                        </div>
                        <div className={`${styles.stat} ${styles.statDisabled}`}>
                            <span className={styles.statNum}>{disabledFeatures}</span>
                            <span className={styles.statLabel}>Disabled</span>
                        </div>
                    </div>
                </div>

                {/* Search */}
                <div className={styles.searchBar}>
                    <FiSearch size={13} className={styles.searchIcon} />
                    <input
                        type="text"
                        placeholder="Search actions or pages..."
                        value={searchQuery}
                        onChange={e => setSearchQuery(e.target.value)}
                        className={styles.searchInput}
                    />
                    {searchQuery && (
                        <button className={styles.searchClear} onClick={() => setSearchQuery('')}>
                            <FiX size={12} />
                        </button>
                    )}
                </div>

                {/* Body */}
                <div className={styles.featureBody}>
                    {isLoadingData ? (
                        <div className={styles.loadingState}>
                            <FiLoader size={18} className={styles.spinIcon} />
                            <span>Loading access data...</span>
                        </div>
                    ) : !selectedStore ? (
                        <div className={styles.emptyState}>
                            <FiLayers size={22} />
                            <span>Select a location to manage access</span>
                        </div>
                    ) : pages.length === 0 ? (
                        <div className={styles.emptyState}>
                            <FiAlertCircle size={22} />
                            <span>{searchQuery ? 'No results found' : 'No features configured'}</span>
                        </div>
                    ) : (
                        pages.map((page) => {
                            const features = groupedByPage[page]
                            const pageEnabled = features.filter(f => toBool(f.isAccessible)).length
                            const isExpanded = expandedPages[page] !== false

                            return (
                                <div key={page} className={styles.pageGroup}>
                                    {/* Page header */}
                                    <button
                                        className={styles.pageGroupHeader}
                                        onClick={() => togglePage(page)}
                                    >
                                        <div className={styles.pageGroupLeft}>
                                            <FiZap size={11} className={styles.pageIcon} />
                                            <span className={styles.pageName}>{page}</span>
                                            <span className={styles.pageCount}>
                                                {pageEnabled}/{features.length}
                                            </span>
                                        </div>
                                        <FiChevronRight
                                            size={13}
                                            className={`${styles.pageChevron} ${isExpanded ? styles.pageChevronOpen : ''}`}
                                        />
                                    </button>

                                    {/* Feature rows */}
                                    {isExpanded && (
                                        <div className={styles.featureList}>
                                            {features.map((feature, idx) => {
                                                const isToggling = togglingId === feature.featureIdentifier
                                                // Normalise the string "true"/"false" from API to boolean
                                                const isOn = toBool(feature.isAccessible)

                                                return (
                                                    <div
                                                        key={idx}
                                                        className={`${styles.featureRow} ${isOn ? styles.featureRowOn : styles.featureRowOff}`}
                                                    >
                                                        <div className={styles.featureInfo}>
                                                            <span className={styles.featureNameText}>
                                                                {feature.featureLabel}
                                                            </span>
                                                            <span className={`${styles.featureStatus} ${isOn ? styles.featureStatusOn : styles.featureStatusOff}`}>
                                                                {isOn ? 'Allowed' : 'Restricted'}
                                                            </span>
                                                        </div>

                                                        <button
                                                            className={`${styles.toggleBtn} ${isToggling ? styles.toggleBtnBusy : ''}`}
                                                            onClick={() => toggleFeatureAccessStatus(
                                                                feature.featureIdentifier,
                                                                feature,
                                                                isOn   // pass normalised boolean, not raw string
                                                            )}
                                                            disabled={isToggling}
                                                            title={isOn ? 'Click to restrict' : 'Click to allow'}
                                                        >
                                                            {isToggling ? (
                                                                <FiLoader size={18} className={styles.spinIcon} />
                                                            ) : isOn ? (
                                                                <FiToggleRight size={22} className={styles.toggleOn} />
                                                            ) : (
                                                                <FiToggleLeft size={22} className={styles.toggleOff} />
                                                            )}
                                                        </button>
                                                    </div>
                                                )
                                            })}
                                        </div>
                                    )}
                                </div>
                            )
                        })
                    )}
                </div>
            </main>
        </div>
    )
}

export default ActionAccessManagement